/**
 * Action executors + human-readable summaries for the chat agent.
 *
 * `summarizeAction` produces the one-line label shown on a confirmation
 * card. `runAction` performs the actual write by delegating to the
 * existing typed db adapters and returns a short success string for the
 * chat transcript. Nothing here runs until the user confirms — the
 * ChatView is responsible for gating on the confirm click.
 */

import {
  createPerson,
  deletePerson,
  logPersonActivity,
  getPerson,
  updatePerson,
} from '../db/people';
import { moveWorkflowStage, getAccount, updateAccount } from '../db/accounts';
import { createDocument, deleteDocument } from '../db/documents';
import {
  createTicket,
  setTicketStatus,
  getTicket,
  updateTicket,
  deleteTicket,
} from '../db/tickets';
import { createTask, deleteTask } from '../db/tasks';
import { getContentItem, updateContentItem } from '../db/contentItems';
import { describePublishOutcome, publishChannelLabel, publishContent } from './contentEngine';
import { db } from '../db/store';
import { resolveCustomerUpdateRecipient } from '../cs/weeklyUpdate';
import {
  putBrainFact,
  getBrainFact,
  restoreBrainFact,
  removeBrainFact,
  sectionLabel,
} from '../brain/companyBrain';
import { nowIso, uuid } from '../db/store';
import { fillTemplate } from '../crm/sequenceRunner';
import { DRAFT_INTENT_LABEL } from './draftFirst';
import type { AgentAction, UpdateBrainAction } from './types';
import type { OutboxDoc, OutreachEnrollment } from '../db/types';
import { appendLedgerSafe } from './ledger';

const ACTOR = 'Argo agent';

function usd(n?: number): string {
  if (n == null) return '';
  return n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });
}

/** One-line description shown on the confirm card and as a verb label. */
export function summarizeAction(action: AgentAction): {
  verb: string;
  detail: string;
} {
  switch (action.kind) {
    case 'create_person': {
      const bits = [action.company && `at ${action.company}`, action.value && `· ${usd(action.value)}`]
        .filter(Boolean)
        .join(' ');
      return {
        verb: action.category === 'customer' ? 'Add customer' : 'Add prospect',
        detail: `${action.name}${bits ? ` ${bits}` : ''}`,
      };
    }
    case 'set_prospect_stage':
      return {
        verb: 'Move prospect',
        detail: `${action.personName} → ${action.stage}`,
      };
    case 'set_customer_status':
      return {
        verb: 'Set customer status',
        detail: `${action.personName} → ${action.status}`,
      };
    case 'create_document':
      return {
        verb: action.docKind === 'proposal' ? 'Create proposal' : 'Create contract',
        detail: `for ${action.entityName}${action.value ? ` · ${usd(action.value)}` : ''}`,
      };
    case 'move_workflow_stage':
      return {
        verb: 'Advance workflow',
        detail: `${action.accountName} · "${action.workflowName}": ${action.fromStage} → ${action.toStage}`,
      };
    case 'create_ticket':
      return {
        verb: 'Open ticket',
        detail: `${action.title}${action.priority ? ` (${action.priority})` : ''}`,
      };
    case 'set_ticket_status':
      return {
        verb: 'Update ticket',
        detail: `${action.ticketTitle} → ${action.status}`,
      };
    case 'assign_ticket':
      return {
        verb: 'Assign ticket',
        detail: `${action.ticketTitle} → @${action.owner}${
          action.fromOwner ? ` (was @${action.fromOwner})` : ''
        }`,
      };
    case 'create_task': {
      const tieTo = action.personName || action.accountName;
      const bits = [
        tieTo && `for ${tieTo}`,
        action.value != null && `· ${usd(action.value)} at risk`,
        action.assignee && `· @${action.assignee}`,
        action.dueDate && `· due ${action.dueDate}`,
        action.priority && action.priority !== 'medium' && `· ${action.priority}`,
      ]
        .filter(Boolean)
        .join(' ');
      return {
        verb: 'Add task',
        detail: `${action.title}${bits ? ` ${bits}` : ''}`,
      };
    }
    case 'schedule_follow_up':
      return {
        verb: 'Schedule follow-up',
        detail: `${action.personName}${action.followUpDate ? ` · ${action.followUpDate}` : ''}${
          action.note ? ` — ${action.note}` : ''
        }`,
      };
    case 'log_activity':
      return {
        verb: `Log ${action.activityKind}`,
        detail: `${action.personName}: ${action.text}`,
      };
    case 'update_brain':
      return {
        verb: 'Update Brain',
        detail: `${action.accountName} · ${sectionLabel(action.section)}: ${action.label}`,
      };
    case 'update_record_field':
      return {
        verb: 'Edit field',
        detail: `${action.recordName} · ${action.attr}: ${action.from ?? '—'} → ${action.to}`,
      };
    case 'send_customer_update':
      return {
        verb: 'Send weekly update',
        detail: `${action.personName}${action.to ? ` → ${action.to}` : ''} · “${action.subject}”`,
      };
    case 'send_drafted_email':
      return {
        verb: `Send ${DRAFT_INTENT_LABEL[action.intent]}`,
        detail: `${action.personName}${action.to ? ` → ${action.to}` : ''} · “${action.subject}”`,
      };
    case 'send_sequence_step':
      return {
        verb: 'Send sequence email',
        detail: `${action.personName} · "${action.sequenceName}" step ${action.stepIndex + 1}: ${action.subjectTemplate}`,
      };
    case 'mark_enrollment_replied':
      return {
        verb: 'Mark replied',
        detail: `${action.personName} · "${action.sequenceName}" — stop sequence`,
      };
    case 'publish_content': {
      // Born-scored honesty on the confirm card: the score (or UNSCORED),
      // the below-bar flag, and an explicit dry-run marker when set.
      const score = action.aqiTotal == null ? 'UNSCORED' : `AQI ${action.aqiTotal}`;
      return {
        verb: 'Publish content',
        detail: `${action.draftTitle ?? action.draftId} → ${publishChannelLabel(action.channel)} · ${score}${
          action.belowThreshold ? ' (below bar)' : ''
        }${action.dryRun === true ? ' · dry run' : ''}`,
      };
    }
  }
}

/**
 * Confidence for an `update_brain` write, honestly scaled per provenance.
 * The Brain stores confidence 0–1; a worker may send a percentage (e.g. 65),
 * so values > 1 are read as percent. Inferred (model-proposed) facts carry
 * the confidence the proposal claimed but are HARD-CAPPED at 0.8 — a model
 * inference can never enter the Brain outranking a confirmed human fact.
 * Operator writes keep the long-standing confident default of 0.9.
 */
export function brainWriteConfidence(
  action: Pick<UpdateBrainAction, 'confidence' | 'source'>,
): number {
  const inferred = action.source === 'inferred';
  const raw = action.confidence ?? (inferred ? 0.6 : 0.9);
  const scaled = raw > 1 ? raw / 100 : raw;
  return inferred ? Math.min(scaled, 0.8) : scaled;
}

/** Add `days` to today and return a plain ISO date (YYYY-MM-DD). */
function isoDatePlus(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

/**
 * Execute a confirmed action and capture an undo function. Returns both the
 * success message and a best-effort undo callback. Undo is null for actions
 * that cannot be safely reversed (e.g. log_activity appends to a timeline;
 * removing a single entry is fragile). For create-type actions, undo deletes
 * the created record. For update-type actions, undo restores the prior field
 * values by reading the record before the mutation.
 */
export async function runActionWithUndo(
  action: AgentAction,
  actor: string = 'operator',
): Promise<{
  result: string;
  undo: (() => Promise<void>) | null;
}> {
  const executed = await executeAction(action);
  const { verb, detail } = summarizeAction(action);
  const summary = [verb, detail].filter(Boolean).join(' — ');

  // Forward entry — the action genuinely executed above. The ledger only
  // observes; a ledger failure must never break the action (appendLedgerSafe).
  await appendLedgerSafe({
    actor,
    kind: 'action',
    actionType: action.kind,
    summary,
    payload: action,
  });

  // Reversal entry — appended only if/when the undo is actually invoked.
  // Reversals are NEW entries; prior entries are never mutated or deleted.
  const wrappedUndo = executed.undo
    ? async () => {
        await executed.undo!();
        await appendLedgerSafe({
          actor,
          kind: 'reversal',
          actionType: action.kind,
          summary: `Reversed: ${summary}`,
          payload: action,
        });
      }
    : null;

  return { result: executed.result, undo: wrappedUndo };
}

/** Perform the write and capture a raw (unledgered) undo. Internal — the
 *  ledger hook lives in {@link runActionWithUndo}, the public seam. */
async function executeAction(action: AgentAction): Promise<{
  result: string;
  undo: (() => Promise<void>) | null;
}> {
  switch (action.kind) {
    case 'create_person': {
      const person = await createPerson({
        category: action.category,
        name: action.name,
        email: action.email,
        company: action.company,
        title: action.title,
        ownerName: action.ownerName,
        ...(action.category === 'prospect'
          ? { stage: action.stage ?? 'new', estimatedValue: action.value }
          : { status: action.status ?? 'active', accountValue: action.value }),
      });
      const createdId = person.id;
      return {
        result: `Added ${action.category} “${person.name}”.`,
        undo: async () => {
          await deletePerson(createdId);
        },
      };
    }
    case 'set_prospect_stage': {
      const before = await getPerson(action.personId);
      const priorStage = before?.stage;
      await updatePerson(action.personId, { stage: action.stage });
      await logPersonActivity(action.personId, 'stage', `Stage → ${action.stage}`, ACTOR);
      return {
        result: `Moved ${action.personName} to ${action.stage}.`,
        undo:
          priorStage != null
            ? async () => {
                await updatePerson(action.personId, { stage: priorStage });
              }
            : null,
      };
    }
    case 'set_customer_status': {
      const before = await getPerson(action.personId);
      const priorStatus = before?.status;
      await updatePerson(action.personId, { status: action.status });
      await logPersonActivity(action.personId, 'system', `Status → ${action.status}`, ACTOR);
      return {
        result: `Set ${action.personName} to ${action.status}.`,
        undo:
          priorStatus != null
            ? async () => {
                await updatePerson(action.personId, { status: priorStatus });
              }
            : null,
      };
    }
    case 'create_document': {
      const doc = await createDocument({
        kind: action.docKind,
        entityType: action.entityType,
        entityId: action.entityId,
        entityName: action.entityName,
        contactName: action.contactName,
        ownerName: action.ownerName,
        value: action.value,
        createdBy: ACTOR,
      });
      const createdId = doc.id;
      return {
        result: `Created ${action.docKind} “${doc.title}”.`,
        undo: async () => {
          await deleteDocument(createdId);
        },
      };
    }
    case 'move_workflow_stage': {
      const before = await getAccount(action.accountId);
      const workflow = before?.workflows.find((w) => w.id === action.workflowId);
      const priorStage = workflow?.stage;
      await moveWorkflowStage(action.accountId, action.workflowId, action.toStage, ACTOR);
      return {
        result: `Advanced “${action.workflowName}” to ${action.toStage}.`,
        undo:
          priorStage != null
            ? async () => {
                await moveWorkflowStage(
                  action.accountId,
                  action.workflowId,
                  priorStage,
                  ACTOR,
                );
              }
            : null,
      };
    }
    case 'create_ticket': {
      const ticket = await createTicket({
        title: action.title,
        spec: action.spec,
        priority: action.priority,
        owner: action.owner ?? null,
        createdBy: ACTOR,
      });
      const createdId = ticket.id;
      return {
        result: `Opened ${ticket.id}: “${ticket.title}”.`,
        undo: async () => {
          await deleteTicket(createdId);
        },
      };
    }
    case 'set_ticket_status': {
      const before = await getTicket(action.ticketId);
      const priorStatus = before?.status;
      await setTicketStatus(action.ticketId, action.status, ACTOR);
      return {
        result: `${action.ticketId} → ${action.status}.`,
        undo:
          priorStatus != null
            ? async () => {
                await setTicketStatus(action.ticketId, priorStatus, ACTOR);
              }
            : null,
      };
    }
    case 'assign_ticket': {
      const before = await getTicket(action.ticketId);
      const priorOwner = before ? before.owner : undefined;
      await updateTicket(action.ticketId, { owner: action.owner }, ACTOR);
      return {
        result: `${action.ticketId} → @${action.owner}.`,
        undo:
          priorOwner !== undefined
            ? async () => {
                await updateTicket(action.ticketId, { owner: priorOwner }, ACTOR);
              }
            : null,
      };
    }
    case 'create_task': {
      const task = await createTask({
        title: action.title,
        priority: action.priority,
        assignee: action.assignee,
        dueDate: action.dueDate,
        personId: action.personId,
        personName: action.personName,
        accountId: action.accountId,
        accountName: action.accountName,
        createdBy: ACTOR,
      });
      const createdId = task.id;
      const tieTo = action.personName || action.accountName;
      return {
        result: `Added task “${task.title}”${tieTo ? ` for ${tieTo}` : ''}.`,
        undo: async () => {
          await deleteTask(createdId);
        },
      };
    }
    case 'schedule_follow_up': {
      const before = await getPerson(action.personId);
      const priorFollowUpDate = before?.followUpDate;
      const priorNextStep = before?.nextStep;
      const followUpDate = action.followUpDate ?? isoDatePlus(3);
      const nextStep = action.note ?? `Follow up with ${action.personName}`;
      await updatePerson(action.personId, { followUpDate, nextStep });
      await logPersonActivity(
        action.personId,
        'task',
        `Follow-up scheduled for ${followUpDate}${action.note ? ` — ${action.note}` : ''}`,
        ACTOR,
      );
      return {
        result: `Follow-up with ${action.personName} set for ${followUpDate}.`,
        undo: async () => {
          const patch: Record<string, any> = {};
          if (priorFollowUpDate !== undefined) patch.followUpDate = priorFollowUpDate;
          if (priorNextStep !== undefined) patch.nextStep = priorNextStep;
          if (Object.keys(patch).length > 0) {
            await updatePerson(action.personId, patch);
          }
        },
      };
    }
    case 'log_activity': {
      await logPersonActivity(action.personId, action.activityKind, action.text, ACTOR);
      // log_activity appends to a timeline. Removing a single entry is fragile
      // (would need to know the entry id beforehand), so no undo.
      return {
        result: `Logged ${action.activityKind} for ${action.personName}.`,
        undo: null,
      };
    }
    case 'update_brain': {
      // Capture the prior head BEFORE the write so undo can restore it
      // verbatim (or remove the fact if this write created it).
      const prior = await getBrainFact(
        action.accountId,
        action.section,
        action.field,
      );
      const source = action.source ?? 'operator';
      const res = await putBrainFact({
        accountId: action.accountId,
        section: action.section,
        field: action.field,
        label: action.label,
        value: action.value,
        source,
        confidence: brainWriteConfidence(action),
        evidence: action.evidence,
        setBy: ACTOR,
      });
      if (!res.written) {
        // Conflict guard refused: a higher-confidence fact from a different
        // source stands. Nothing changed, so there is nothing to undo —
        // surfaced honestly rather than pretending the write landed.
        return {
          result: `Kept ${action.accountName}’s existing ${sectionLabel(
            action.section,
          )} fact — a higher-confidence ${res.fact.source} value stands (logged as a conflict to resolve).`,
          undo: null,
        };
      }
      return {
        result: `Updated ${action.accountName}’s Brain — ${sectionLabel(
          action.section,
        )}: ${action.label}.`,
        undo: prior
          ? async () => {
              await restoreBrainFact(prior);
            }
          : async () => {
              await removeBrainFact(
                action.accountId,
                action.section,
                action.field,
              );
            },
      };
    }
    case 'update_record_field': {
      // Governed inline cell edit from the CRM grid. Patch through the
      // existing typed adapters so timestamp/audit stamping stays in one
      // place; capture the prior value first so undo restores it verbatim.
      if (action.recordType === 'account') {
        const before = await getAccount(action.recordId);
        const prior = before
          ? ((before as unknown as Record<string, unknown>)[action.attr] as
              | string
              | undefined)
          : undefined;
        await updateAccount(action.recordId, { [action.attr]: action.to });
        return {
          result: `Updated ${action.recordName}: ${action.attr} → ${action.to}.`,
          undo:
            before !== null
              ? async () => {
                  await updateAccount(action.recordId, {
                    [action.attr]: prior ?? action.from ?? undefined,
                  });
                }
              : null,
        };
      }
      const before = await getPerson(action.recordId);
      const prior = before
        ? ((before as unknown as Record<string, unknown>)[action.attr] as
            | string
            | undefined)
        : undefined;
      await updatePerson(action.recordId, { [action.attr]: action.to });
      await logPersonActivity(
        action.recordId,
        'system',
        `${action.attr} → ${action.to}`,
        ACTOR,
      );
      return {
        result: `Updated ${action.recordName}: ${action.attr} → ${action.to}.`,
        undo:
          before !== null
            ? async () => {
                await updatePerson(action.recordId, {
                  [action.attr]: prior ?? action.from ?? undefined,
                });
              }
            : null,
      };
    }
    case 'send_customer_update': {
      // Governed weekly customer update → outbox. Resolve the recipient from
      // the action (stamped at proposal time) or the LIVE record — and when
      // neither has an email, surface the honest "needs email" state and
      // write NOTHING. Never a guessed address, never a silent drop.
      const person = await getPerson(action.personId);
      const to =
        action.to ??
        (person ? resolveCustomerUpdateRecipient(person)?.email : undefined);
      if (!to) {
        return {
          result:
            `Needs email: no contact email on file for ${action.personName} — ` +
            `nothing was sent. Add a POC email and re-propose.`,
          undo: null,
        };
      }
      const ts = new Date().toISOString();
      const outboxId = crypto.randomUUID?.() || `update-${Date.now()}`;
      await db.add('outbox', {
        id: outboxId,
        to,
        subject: action.subject,
        textContent: action.text,
        htmlContent: action.html,
        status: 'queued',
        createdAt: ts,
        updatedAt: ts,
      });
      // Stamp the cadence marker + timeline entry on the customer record.
      const priorSentAt = person?.lastUpdateSentAt;
      if (person) {
        await updatePerson(action.personId, { lastUpdateSentAt: ts });
        await logPersonActivity(
          action.personId,
          'email',
          `Weekly update queued to ${to} — “${action.subject}”`,
          ACTOR,
        );
      }
      return {
        result: `Weekly update for ${action.personName} queued to ${to}.`,
        undo: async () => {
          // Cancel the outbox doc only while it is still queued — once the
          // send function picked it up, the email left the building.
          const doc = await db.get('outbox', outboxId);
          if (doc && (doc.status ?? 'queued') === 'queued') {
            await db.remove('outbox', outboxId);
          }
          if (person) {
            await updatePerson(action.personId, { lastUpdateSentAt: priorSentAt });
          }
        },
      };
    }
    case 'send_drafted_email': {
      // Draft-first suggestion approved → outbox. Same mechanics as
      // send_customer_update: resolve the recipient from the action (stamped
      // at proposal time) or the LIVE record — and when neither has an
      // email, surface the honest "needs email" state and write NOTHING.
      // Never a guessed address, never a silent drop.
      const person = await getPerson(action.personId);
      const to =
        action.to ??
        (person ? resolveCustomerUpdateRecipient(person)?.email : undefined);
      if (!to) {
        return {
          result:
            `Needs email: no contact email on file for ${action.personName} — ` +
            `nothing was sent. Add a POC email and re-propose.`,
          undo: null,
        };
      }
      const intentLabel = DRAFT_INTENT_LABEL[action.intent];
      const ts = new Date().toISOString();
      const outboxId = crypto.randomUUID?.() || `draft-${Date.now()}`;
      await db.add('outbox', {
        id: outboxId,
        to,
        subject: action.subject,
        textContent: action.text,
        htmlContent: action.html,
        status: 'queued',
        createdAt: ts,
        updatedAt: ts,
      });
      if (person) {
        await logPersonActivity(
          action.personId,
          'email',
          `${intentLabel[0].toUpperCase()}${intentLabel.slice(1)} queued to ${to} — “${action.subject}”`,
          ACTOR,
        );
      }
      return {
        result: `${intentLabel[0].toUpperCase()}${intentLabel.slice(1)} for ${action.personName} queued to ${to}.`,
        undo: async () => {
          // Cancel the outbox doc only while it is still queued — once the
          // send function picked it up, the email left the building.
          const doc = await db.get('outbox', outboxId);
          if (doc && (doc.status ?? 'queued') === 'queued') {
            await db.remove('outbox', outboxId);
          }
        },
      };
    }
    case 'send_sequence_step': {
      // Read the sequence so we know how many steps there are (→ completion)
      // and the enrollment so we advance from its real current state.
      const [sequence, enrollment] = await Promise.all([
        db.get('outreachSequences', action.sequenceId),
        db.get('outreachEnrollments', action.enrollmentId),
      ]);
      if (!enrollment) {
        return { result: `Enrollment ${action.enrollmentId} no longer exists.`, undo: null };
      }
      const priorStep = enrollment.currentStep;
      const priorStatus = enrollment.status;
      const priorLastSentAt = enrollment.lastSentAt;

      // Personalize with the contact's name; the outbox doc is what the
      // sendEmail Cloud Function delivers — nothing sends until it's queued.
      const contact = { name: action.personName };
      const subject = fillTemplate(action.subjectTemplate, contact);
      const body = fillTemplate(action.bodyTemplate, contact);
      const ts = nowIso();
      const outboxDoc: OutboxDoc = {
        id: uuid(),
        to: action.toEmail ?? '',
        subject,
        textContent: body,
        htmlContent: body,
        status: 'queued',
        createdAt: ts,
        updatedAt: ts,
      };
      await db.add('outbox', outboxDoc);
      const outboxId = outboxDoc.id;

      // Advance the enrollment: next step, stamp lastSentAt, complete when we
      // just sent the final step.
      const stepCount = sequence?.steps.length ?? action.stepIndex + 1;
      const nextStep = priorStep + 1;
      const completed = nextStep >= stepCount;
      await db.update('outreachEnrollments', action.enrollmentId, {
        currentStep: nextStep,
        lastSentAt: ts,
        status: completed ? 'completed' : 'active',
        updatedAt: ts,
      });

      return {
        result: `Queued "${subject}" to ${action.personName}${
          completed ? ' — sequence completed.' : '.'
        }`,
        undo: async () => {
          // Reverse the advance; cancel the outbox doc if it hasn't sent yet.
          const patch: Partial<OutreachEnrollment> = {
            currentStep: priorStep,
            status: priorStatus,
            updatedAt: nowIso(),
          };
          // Restore prior lastSentAt honestly (may be undefined → clear).
          patch.lastSentAt = priorLastSentAt;
          await db.update('outreachEnrollments', action.enrollmentId, patch);
          const doc = await db.get('outbox', outboxId);
          if (doc && doc.status !== 'sent') {
            await db.update('outbox', outboxId, {
              status: 'error',
              error: 'Cancelled — sequence send undone before delivery.',
              updatedAt: nowIso(),
            });
          }
        },
      };
    }
    case 'mark_enrollment_replied': {
      const enrollment = await db.get('outreachEnrollments', action.enrollmentId);
      if (!enrollment) {
        return { result: `Enrollment ${action.enrollmentId} no longer exists.`, undo: null };
      }
      const priorStatus = enrollment.status;
      const priorReason = enrollment.stoppedReason;
      await db.update('outreachEnrollments', action.enrollmentId, {
        status: 'stopped',
        stoppedReason: 'replied',
        updatedAt: nowIso(),
      });
      return {
        result: `Marked ${action.personName} as replied — stopped "${action.sequenceName}".`,
        undo: async () => {
          await db.update('outreachEnrollments', action.enrollmentId, {
            status: priorStatus,
            stoppedReason: priorReason,
            updatedAt: nowIso(),
          });
        },
      };
    }
    case 'publish_content': {
      // Publishing happens ONLY through the engine seam (POST /publish via
      // our proxy) — there is no local simulation of publishing, and the
      // seam never claims 'published' unless the engine said so. `dryRun`
      // is forwarded only when explicitly set: omitted, the engine's
      // server-side dry-run safety default rules.
      const outcome = await publishContent({
        content: action.contentSnapshot,
        channel: action.channel,
        ...(action.customer ? { customer: action.customer } : {}),
        ...(typeof action.dryRun === 'boolean' ? { dryRun: action.dryRun } : {}),
      });
      const described = describePublishOutcome(outcome, action.channel);
      const result = described.endsWith('.') ? described : `${described}.`;
      // Record the engine's honest outcome on the draft record. Best-effort:
      // the draft may have been deleted since proposal time — the action
      // result above stays the source of truth either way.
      try {
        const existing = await getContentItem(action.draftId);
        if (existing) {
          await updateContentItem(action.draftId, {
            // Only a REAL live post moves the kanban to 'published' — a dry
            // run, error, or missing engine leaves the stage untouched.
            ...(outcome.status === 'published' ? { status: 'published' as const } : {}),
            lastPublish: {
              status: outcome.status,
              url: outcome.url,
              message: outcome.message,
              channel: action.channel,
              at: nowIso(),
            },
          });
        }
      } catch {
        // Draft stamp is observational; never mask the honest result.
      }
      // Publishing is external — a live post cannot be recalled. No undo.
      return { result, undo: null };
    }
  }
}

/**
 * Whether an action kind can be reversed after it executes.
 *
 * WHY this exists separately from `runActionWithUndo`: that function performs
 * the write *before* it can hand back an undo, so inspecting its returned
 * `undo` to decide whether something was safe to auto-run is too late — the
 * side effect already happened. Auto-run (autonomy === 'auto') must therefore
 * gate on the kind *up front*. `log_activity` appends a timeline entry with no
 * recoverable id, so it can never be undone and must never auto-execute
 * (otherwise it would write while still showing a "pending" card, inviting a
 * manual Confirm that writes it a second time). `publish_content` is external
 * by definition — a live social post left the building and cannot be
 * recalled — so it is registered non-undoable the same way.
 */
export function isUndoableKind(kind: AgentAction['kind']): boolean {
  return kind !== 'log_activity' && kind !== 'publish_content';
}

/** Execute a confirmed action. Backward-compatible wrapper around runActionWithUndo. */
export async function runAction(action: AgentAction): Promise<string> {
  const { result } = await runActionWithUndo(action);
  return result;
}

/** Risk tier for a proposed agent action. See `actionRisk`. */
export type ActionRisk = 'auto' | 'gated';

/**
 * The only kinds eligible for the 'auto' tier. Everything not in this
 * allowlist is 'gated' — including any future kind added to the union,
 * which fails safe to 'gated' until explicitly reviewed and added here.
 */
export const RISK_AUTO_KINDS: ReadonlySet<AgentAction['kind']> = new Set<
  AgentAction['kind']
>(['create_task', 'schedule_follow_up', 'log_activity', 'update_brain']);

/**
 * Classify a proposed action's risk tier — the foundation for risk-tiered
 * approval. NOT wired into execution or approval yet; pure classifier only.
 *
 * Policy (conservative by construction — when unsure, 'gated'):
 *
 * - 'auto': clearly low-stakes, internal, low-blast-radius writes that a
 *   tiered-approval flow could safely run without an explicit gate:
 *   `create_task`, `schedule_follow_up`, `log_activity`, `update_brain`.
 *   These only touch internal planning/notes state; the worst-case mistake
 *   is a stray task, follow-up date, timeline note, or Brain fact (which is
 *   itself protected by the Brain's no-silent-downgrade conflict guard).
 *   Note this tier is about blast radius, not mechanical undoability —
 *   `log_activity` has no undo (see `isUndoableKind`) but is an append-only
 *   internal note; auto-run wiring may still consult `isUndoableKind`
 *   separately.
 *
 * - 'gated': anything irreversible, external-effecting, money- or
 *   stage-moving, or destructive. That includes pipeline/lifecycle moves
 *   (`set_prospect_stage` — especially closed-won/closed-lost,
 *   `set_customer_status`, `move_workflow_stage`), record creation with
 *   money or external surface (`create_person`, `create_document` —
 *   proposals/contracts are customer-facing), and ticket mutations that
 *   change ownership or state others depend on (`create_ticket`,
 *   `set_ticket_status`, `assign_ticket`). Unknown/future kinds also land
 *   here by default (allowlist, not blocklist).
 */
export function actionRisk(action: AgentAction): ActionRisk {
  return RISK_AUTO_KINDS.has(action.kind) ? 'auto' : 'gated';
}
